import React, { useMemo } from 'react'
import { useSelector } from 'react-redux'
import { Button } from 'antd'
import { Icon } from '@iconify/react'
import { useNavigate, useLocation } from 'react-router-dom'
import { isProjectWidgetMode, PRIVATE_ROUTES } from '../../../routes'
import { loginState } from '../../login/login.slice'
import { projectsState } from '../projects.slice'
import styles from './ProjectAccessDeniedView.module.css'

const DEFAULT_MESSAGE = "You don't have access to this project"

const ProjectAccessDeniedView = ({
    message = DEFAULT_MESSAGE,
    activeProjectId,
    currentProjectId,
}) => {
    const navigate = useNavigate()
    const location = useLocation()
    const isWidgetMode = isProjectWidgetMode(location.search)
    const { userProfile } = useSelector(loginState)
    const { clientModalTotalProjects } = useSelector(projectsState)

    const showBrowseProjectsButton = useMemo(() => {
        const isSuperOrExistingUser = Boolean(
            userProfile?.isSuperAdmin || userProfile?.isExistingUser
        )
        return (
            isSuperOrExistingUser ||
            (userProfile?.isSupportUser &&
                Number(clientModalTotalProjects) > 5)
        )
    }, [userProfile, clientModalTotalProjects])

    const hasAlternateProject =
        activeProjectId && activeProjectId !== currentProjectId

    const showHomeButton = !showBrowseProjectsButton

    const showActions =
        !isWidgetMode &&
        (hasAlternateProject || showBrowseProjectsButton || showHomeButton)

    const goToActiveProject = () => {
        if (hasAlternateProject) {
            navigate(PRIVATE_ROUTES.projects.projectId(activeProjectId))
            return
        }
        navigate(PRIVATE_ROUTES.projects.root)
    }

    return (
        <div className={styles.page} role="alert" aria-live="polite">
            <div className={styles.content}>
                <div className={styles.iconBadge} aria-hidden>
                    <Icon
                        icon="mdi:shield-lock-outline"
                        width={26}
                        height={26}
                        className={styles.icon}
                    />
                </div>

                <h1 className={styles.title}>Access restricted</h1>

                <p className={styles.message}>
                    {message || DEFAULT_MESSAGE}
                </p>

                <p className={styles.hint}>
                    Tasks, files, and project settings are not available for
                    this workspace.
                </p>

                {showActions && (
                    <div className={styles.actions}>
                        {hasAlternateProject && (
                            <Button
                                type="default"
                                className={styles.browseBtnPrimary}
                                icon={
                                    <Icon
                                        icon="mdi:folder-open-outline"
                                        width={18}
                                        height={18}
                                    />
                                }
                                onClick={goToActiveProject}
                            >
                                Go to your project
                            </Button>
                        )}
                        {showBrowseProjectsButton && (
                            <Button
                                type="default"
                                className={
                                    hasAlternateProject
                                        ? styles.browseBtnSecondary
                                        : styles.browseBtnPrimary
                                }
                                icon={
                                    <Icon
                                        icon="mdi:folder-multiple-outline"
                                        width={18}
                                        height={18}
                                    />
                                }
                                onClick={() =>
                                    navigate(PRIVATE_ROUTES.projects.root)
                                }
                            >
                                Browse projects
                            </Button>
                        )}
                        {showHomeButton && (
                            <Button
                                type="default"
                                className={
                                    hasAlternateProject
                                        ? styles.browseBtnSecondary
                                        : styles.browseBtnPrimary
                                }
                                icon={
                                    <Icon
                                        icon="mdi:home-outline"
                                        width={18}
                                        height={18}
                                    />
                                }
                                onClick={() =>
                                    navigate(PRIVATE_ROUTES.home)
                                }
                            >
                                Home
                            </Button>
                        )}
                    </div>
                )}
            </div>
        </div>
    )
}

export default ProjectAccessDeniedView
